import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { inputStyles } from '@/styles/input';
import { cn } from '@/utils/cn';
import { CardBrandIcons } from '../PaymentInputs/CardBrandIcons';
import { FontAwesomeIcon } from '@/components/Common/FontAwesomeIcon/FontAwesomeIcon';
import { InputTaxComponent } from '../InputTaxComponent/InputTaxComponent';
import { Email } from '../Common/Email';
import { shouldHideEmail } from '@/utils/conditionalRendering';
import { useTranslate } from '@/hooks/useTranslate';
import { Loader } from '../Common/Loader';
import { isEmailValid } from '@/utils/email';
import { logError } from '@/utils/logger';
import { CHARGEBEE_FIELD_IDS, CHARGEBEE_FIELD_PLACEHOLDERS, CHARGEBEE_FIELD_STYLES } from '@/constants/chargebee';
import { chargebeeService } from '@/services/chargebeeService';
import { useLatestRef } from '@/hooks/useLatestRef';
import type { ChargebeeCardComponent, ChargebeeFieldState, ChargebeeFieldType } from '@/types/chargebee';
import { trackCardFieldInvalid, trackCardFormStarted } from '@/utils/conversionTracking';

type FieldValidity = Record<ChargebeeFieldType, { complete: boolean; error: string | null }>;

const INITIAL_FIELD_STATE: FieldValidity = {
  number: { complete: false, error: null },
  expiry: { complete: false, error: null },
  cvv: { complete: false, error: null },
};

// Chargebee shrink-wraps each field iframe to its line box (~24px) instead of
// filling the host, so the host has to center it inside the 44px `inputStyles`
// row — otherwise the text renders flush against the top border.
const CHARGEBEE_FIELD_HOST_CLASS = 'flex flex-col justify-center w-full h-full';

// Same localized copy the Stripe form maps its field errors to.
const FIELD_ERROR_KEYS: Record<ChargebeeFieldType, string> = {
  number: 'fieldErrors.invalidCard',
  expiry: 'fieldErrors.invalidExpirationDate',
  cvv: 'fieldErrors.invalidSecurityCode',
};

/**
 * Chargebee Card Component in FIELDS mode — subscription checkout only.
 * Structurally a copy of {@link StripeCardForm}: identical wrappers
 * (`inputStyles` owns border/radius/height, red-500 border + error line on
 * invalid), identical `gap-3`/`gap-4` layout, same `InputTaxComponent` +
 * `Email` tail. Chargebee only paints the text INSIDE each iframe
 * (`CHARGEBEE_FIELD_STYLES`), which is what makes the two forms pixel-match.
 */
export const ChargebeeCardForm = () => {
  const context = useContext(CheckoutContext);
  const {
    customer,
    featureFlags,
    template,
    subscription,
    actions: { setIsFormValid },
    card: { setCardElementRef },
  } = context;
  const { t } = useTranslate();
  const [fields, setFields] = useState<FieldValidity>(INITIAL_FIELD_STATE);
  const [isZipCodeValid, setIsZipCodeValid] = useState(false);
  const [mountError, setMountError] = useState<string | null>(null);
  const cardComponentRef = useRef<ChargebeeCardComponent | null>(null);
  const isMountedRef = useRef(false);
  // Latest context/t kept in refs so the mount effect's identity stays stable:
  // `useTranslate` returns a NEW `t` every render, and depending on it would
  // tear down + remount the Chargebee fields on every context change (which
  // loops: remount → setCardElementRef → new context → remount).
  const contextRef = useLatestRef(context);
  const tRef = useLatestRef(t);

  const cbInstance = subscription?.cbInstance;

  const handleFieldChange = useCallback(
    (state: ChargebeeFieldState) => {
      const field = state.field;
      setFields((prev) => ({
        ...prev,
        [field]: {
          complete: state.complete,
          error: state.error ? tRef.current(FIELD_ERROR_KEYS[field]) : null,
        },
      }));
      if (state.error) trackCardFieldInvalid(contextRef.current, field === 'cvv' ? 'cvc' : field);
    },
    [contextRef, tRef]
  );

  useEffect(() => {
    if (!cbInstance || isMountedRef.current) return;
    isMountedRef.current = true;

    const cardComponent = chargebeeService.createCardComponent({
      // We draw <CardBrandIcons/> ourselves, exactly like the Stripe form.
      icon: false,
      placeholder: CHARGEBEE_FIELD_PLACEHOLDERS,
      style: CHARGEBEE_FIELD_STYLES,
    });

    (['number', 'expiry', 'cvv'] as const).forEach((fieldType) => {
      cardComponent
        .createField(fieldType)
        .at(`#${CHARGEBEE_FIELD_IDS[fieldType.toUpperCase() as keyof typeof CHARGEBEE_FIELD_IDS]}`)
        .on('change', handleFieldChange)
        .on('focus', () => trackCardFormStarted(contextRef.current));
    });

    cardComponent
      .mount()
      .then(() => {
        cardComponentRef.current = cardComponent;
        // Same channel every PSP card form uses: the payment handler picks the
        // component up from `checkout.card.cardElementRef` (`authorizeWith3ds`).
        setCardElementRef({ current: cardComponent });
      })
      .catch((error: unknown) => {
        logError('Chargebee card component mount failed', error);
        setMountError(tRef.current('somethingWentWrong'));
      });

    return () => {
      cardComponentRef.current?.destroy?.().catch(() => {});
      cardComponentRef.current = null;
      isMountedRef.current = false;
    };
  }, [cbInstance, handleFieldChange, setCardElementRef, contextRef, tRef]);

  useEffect(() => {
    const isFormValid =
      fields.number.complete &&
      fields.expiry.complete &&
      fields.cvv.complete &&
      !fields.number.error &&
      !fields.expiry.error &&
      !fields.cvv.error &&
      isZipCodeValid &&
      isEmailValid(customer?.email);
    setIsFormValid(isFormValid);
  }, [fields, isZipCodeValid, customer?.email, setIsFormValid]);

  const onTaxValidationChange = useCallback((isValid: boolean) => {
    setIsZipCodeValid(isValid);
  }, []);

  if (!cbInstance) {
    // Chargebee.js is still loading (route-scoped lazy SDK) — mirror
    // StripeCardForm's missing-promise fallback.
    return <Loader />;
  }

  if (mountError) {
    return <div className="text-xs text-red-500">{mountError}</div>;
  }

  return (
    <div className="flex flex-col items-center w-full gap-3">
      <div className="flex flex-col gap-1 w-full">
        <div className="relative w-full">
          <div
            className={cn(inputStyles, 'w-full p-0 pl-2', fields.number.error && 'border-red-500')}
            data-testid="chargebee-card-number-wrapper"
          >
            <div id={CHARGEBEE_FIELD_IDS.NUMBER} className={CHARGEBEE_FIELD_HOST_CLASS} />
          </div>
          <CardBrandIcons />
        </div>
        {fields.number.error && <div className="text-xs text-red-500">{fields.number.error}</div>}
      </div>
      <div className="flex items-start w-full gap-4">
        <div className="flex flex-col gap-1 flex-1">
          <div
            className={cn(inputStyles, 'w-full p-0 pl-2', fields.expiry.error && 'border-red-500')}
            data-testid="chargebee-card-expiry-wrapper"
          >
            <div id={CHARGEBEE_FIELD_IDS.EXPIRY} className={CHARGEBEE_FIELD_HOST_CLASS} />
          </div>
          {fields.expiry.error && <div className="text-xs text-red-500">{fields.expiry.error}</div>}
        </div>
        <div className="flex flex-col gap-1 flex-1">
          <div className="relative w-full">
            <div
              className={cn(inputStyles, 'w-full p-0 pl-2', fields.cvv.error && 'border-red-500')}
              data-testid="chargebee-card-cvv-wrapper"
            >
              <div id={CHARGEBEE_FIELD_IDS.CVV} className={CHARGEBEE_FIELD_HOST_CLASS} />
            </div>
            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
              <FontAwesomeIcon icon="fa-light fa-credit-card" className="text-muted-foreground text-lg" />
            </div>
          </div>
          {fields.cvv.error && <div className="text-xs text-red-500">{fields.cvv.error}</div>}
        </div>
      </div>
      <InputTaxComponent onValidate={onTaxValidationChange} />
      {!shouldHideEmail(featureFlags, customer, template.features) && (
        <div className="w-full">
          <Email />
        </div>
      )}
    </div>
  );
};
